// src/pages/Contract/utils/dismountUpdateContract.ts
import { ContractResponseUpdateDTO } from 'models/ContractResponseUpdateDTO'
import { StepsFormProps } from 'pages/Contracts/Components/StepsForm/interface'
import formatter from 'utils/formatter'
import { removeEmptyValues } from 'utils/removeEmptyValues'

export const dismountUpdateContract = (
    data: ContractResponseUpdateDTO
): StepsFormProps => {
    const { veiculo, devedor, credor, anexos } = data

    // 1. Unificar veículos da resposta (veiculo principal + lista) e remover nulos
    const rawVeiculosList = ((data as any).veiculos ?? []) as Array<any>
    const allVehiclesSource = [veiculo, ...rawVeiculosList].filter(v => !!v && !!v.chassi)

    // 2. Remover duplicatas baseado no Chassi
    const uniqueVehiclesMap = new Map();
    allVehiclesSource.forEach(v => {
        if (!uniqueVehiclesMap.has(v.chassi)) {
            uniqueVehiclesMap.set(v.chassi, v);
        }
    });
    const uniqueVehicles = Array.from(uniqueVehiclesMap.values());

    // 3. Separar o principal (primeiro único) dos adicionais
    const mainVehicleSource = uniqueVehicles[0] || veiculo;
    const additionalVehiclesSource = uniqueVehicles.slice(1);

    const terceiroGarantidor =
        (data as any).terceiroGarantidor ?? (data as any).TerceiroGarantidor

    const normalizeRemarcado = (value: any) => {
        if (value === true || value === 'true' || value === 'SIM' || value === 1 || value === '1') return '1'
        if (value === false || value === 'false' || value === 'NAO' || value === 0 || value === '0') return '0'
        return value
    }

    const formatPercentage = (value: number | string) => {
        if (!value) {
            return ''
        }
        return (
            value
                .toLocaleString('pt-BR', {
                    useGrouping: false,
                    minimumFractionDigits: 3
                })
                .replace('.', ',') + '%'
        )
    }

    const formatCoin = (value: number | string) => {
        if (!value) {
            return ''
        }
        return value
            .toLocaleString('pt-BR', {
                useGrouping: false,
                minimumFractionDigits: 2
            })
            .replace('.', ',')
    }

    // Função auxiliar para formatar um objeto de veículo
    const formatVehicleData = (v: any) => ({
        ...v,
        identificacaoRemarcacao: normalizeRemarcado(
            v?.idRemarcado ?? v?.identificacaoRemarcacao
        ),
        ufGravame: data.ufGravame,
        uf: v?.uf ? v.uf : '-',
        indicativoVeiculoNovo:
            v?.indicativoVeiculoNovo !== null &&
                v?.indicativoVeiculoNovo !== undefined &&
                v?.indicativoVeiculoNovo !== ''
                ? v?.indicativoVeiculoNovo
                : ('not_informed' as any),
        numeroMotor: v?.numeroMotor ?? ''
    });

    return removeEmptyValues({
        vehicles: formatVehicleData(mainVehicleSource),
        vehiclesList: additionalVehiclesSource.map(formatVehicleData),

        contract: {
            dataContrato: data.dataContrato,
            numeroContrato: data.numeroContrato,
            numeroRegistroContrato: data.numeroRegistroContrato,
            numeroGravame: data.numeroGravame,
            quantidadeMeses: data.quantidadeMeses,
            tipoRestricao: data.tipoRestricao,
            dataAditivo: data.dataAditivo,
            opcaoCompra:
                data.opcaoCompra === true
                    ? 'SIM'
                    : data.opcaoCompra === false
                        ? 'NAO'
                        : '',
            numAditivoContratoOrigem: data.numeroAditivo,
            numAditivoContrato: data.registroAditivo,
            tipoAditivo: data.tipoAditivo ?? '',
            dataAlteracao: data.dataAlteracao,
            ufContrato: data.ufContrato,
            municipioContrato: data.municipio.nome,
            status: data.status,
            numeroDudaPago: data.numeroDudaPago,
            ...(['BAIXADO', 'CANCELADO'].includes(data.status)
                ? {
                    descricaoBaixaCancelamento:
                        data.descricaoBaixaCancelamento,
                    dataBaixaCancelamento: formatter.data(
                        data.dataBaixaCancelamento!
                    )
                }
                : {})
        },
        complementary: {
            identificadorTaxaMora: data.idTaxaMora,
            valorTaxaMora: formatPercentage(data.valorTaxaMora),
            identificadorTaxaMulta: data.idTaxaMulta,
            valorTaxaMulta: formatPercentage(data.valorTaxaMulta),
            valorIOF: formatCoin(data.valorIOF),
            valorTaxaJurosMes: formatPercentage(data.valorTaxaJurosMes),
            valorTaxaJurosAno: formatPercentage(data.valorTaxaJurosAno),
            valorTaxaContrato: formatCoin(data.valorTaxaContrato),
            indices: data.indices,
            correcaoMonetaria: data.correcaoMonetaria,
            numeroCertidaoNegativaDebitos: data.numeroCertidaoNegativaDebitos ?? '',
            identificadorComissao: data.idComissao,
            valorComissao: formatCoin(data.valorComissao),
            indicacaoPenalidade: data.indicacaoPenalidade,
            penalidade: data.penalidade,
            principalRecebedorPagamento: data.principalRecebedorPagamento,
            tipoDocumentoRecebedor: data.tipoDocumentoRecebedor,
            documentoPrincipalRecebedorPagamento:
                data.documentoPrincipalRecebedorPagamento,
            tipoDocumentoVendedor: data.tipoDocumentoVendedor,
            documentoVendedor: data.documentoVendedor,
            comentarios: data.comentarios,
            fundoReserva: formatCoin(data.fundoReserva)
        },
        financing: {
            valorTotalDivida: formatCoin(data.valorTotalDivida),
            valorParcela: formatCoin(data.valorParcela),
            dataLiberacaoCredito: data.liberacaoCredito.dataLiberacaoCredito,
            ufLiberacaoCredito: data.liberacaoCredito.ufLiberacaoCredito,
            municipioLiberacaoCredito:
                data.liberacaoCredito.municipioLiberacaoCredito.nome,
            vencimentoPrimeiraParcela: data.vencimentoPrimeiraParcela,
            vencimentoUltimaParcela: data.vencimentoUltimaParcela
        },
        consortium: {
            cotaConsorcio: data.cotaConsorcio,
            numeroGrupo: data.grupoConsorcio
        },
        creditor: { ...credor, razaoSocial: '' },
        debtor: { ...devedor },
        terceiroGarantidor: terceiroGarantidor
            ? {
                nome: terceiroGarantidor.nome ?? '',
                tipoDocumento: terceiroGarantidor.tipoDocumento ?? '',
                documento: terceiroGarantidor.documento ?? '',
                telefone: terceiroGarantidor.telefone ?? '',
                email: terceiroGarantidor.email ?? '',
                endereco: {
                    cep: terceiroGarantidor.endereco?.cep ?? '',
                    logradouro: terceiroGarantidor.endereco?.logradouro ?? '',
                    numero: terceiroGarantidor.endereco?.numero ?? '',
                    complemento: terceiroGarantidor.endereco?.complemento ?? '',
                    bairro: terceiroGarantidor.endereco?.bairro ?? '',
                    municipio: terceiroGarantidor.endereco?.municipio ?? '',
                    uf: terceiroGarantidor.endereco?.uf ?? ''
                }
            }
            : undefined,
        anexos: anexos
    })
}